import React, { useCallback, useState } from 'react';
import { useToast } from '../Components/CommonToast';
import { Body } from '../Constants/Fonts';

// Every SSO endpoint answers HTTP 200 and reports failure in the body, so
// success is read off status_code / error rather than the transport.
export const readError = (res) => res?.statusCode?.error?.message;
export const readData = (res) => res?.statusCode?.response_data?.data;
export const readMessage = (res) => res?.statusCode?.response_data?.message;
export const isOk = (res) => [200, 201].includes(res?.statusCode?.response_data?.status_code) && !readError(res);

export const formatDate = (value) => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString() : '-';
};

export const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const Message = ({ message }) => (
    message?.text
        ? (
            <Body
                className={`mb-0 ${message.type === 'error' ? 'sso_error_text' : 'sso_success_text'}`}
                color={message.type === 'error' ? 'var(--color-danger)' : 'var(--color-success)'}
            >
                {message.text}
            </Body>
        )
        : null
);

export const CopyValue = ({ value, copied, onCopy }) => (
    <span className='sso_txt_record'>
        <code>{value}</code>
        <button type='button' className='sso_copy_btn' onClick={() => onCopy(value)}>
            {copied === value ? 'Copied' : 'Copy'}
        </button>
    </span>
);

// Shared request plumbing built on the app-wide useToast(): raises a sticky
// loading toast, flips a busy flag, then swaps that toast for the API's success
// or failure message — so each caller is left with just its own validation plus
// one call.
//
// Inline messages (say/clearMsg) stay for client-side validation, which belongs
// next to the field rather than in a corner of the screen.
export function useSsoRequest() {
    const [busy, setBusy] = useState({});
    const [msg, setMsg] = useState({});
    const { notify, dismiss, toastNode } = useToast();

    const say = useCallback((key, type, text) => setMsg((prev) => ({ ...prev, [key]: { type, text } })), []);
    const clearMsg = useCallback((key) => setMsg((prev) => (prev[key] ? { ...prev, [key]: null } : prev)), []);

    // The toast always carries what the API said — error.message on failure and
    // response_data.message on success. `success`/`failure` are only fallbacks
    // for the calls that come back without any message of their own.
    const request = useCallback(async (key, call, onSuccess, options = {}) => {
        const { loading = 'Working…', success, failure = 'Could not complete the request. Please try again.' } = options;
        // duration 0 = sticky; dismissed by hand once the call resolves.
        const loadingId = notify(loading, 'info', { duration: 0 });

        try {
            setBusy((prev) => ({ ...prev, [key]: true }));

            const res = await call();
            const apiError = readError(res);

            if (apiError) { dismiss(loadingId); notify(apiError, 'error'); return; }
            if (!isOk(res)) { dismiss(loadingId); notify(readMessage(res) || failure, 'error'); return; }

            await onSuccess(res);
            dismiss(loadingId);
            notify(readMessage(res) || success || 'Done.', 'success');
        } catch (error) {
            console.error(`SSO ${key} request failed:`, error);
            dismiss(loadingId);
            notify('Something went wrong. Please try again.', 'error');
        } finally {
            setBusy((prev) => ({ ...prev, [key]: false }));
        }
    }, [notify, dismiss]);

    return { busy, msg, say, clearMsg, request, notify, toastNode };
}

export function useCopyValue() {
    const [copied, setCopied] = useState('');

    const copy = useCallback(async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(value);
            setTimeout(() => setCopied(''), 2000);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    }, []);

    return { copied, copy };
}
